import React, { useState } from "react";
import {
  isProjectFieldFileContentUrl,
  openAuthenticatedProjectFieldFile,
} from "../projectFieldFileAuth";

export function fileUrlFromStoredValue(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object") {
    if (typeof v.url === "string" && v.url.trim()) return v.url.trim();
    if (typeof v.site_walk_file === "string" && v.site_walk_file.trim()) {
      return v.site_walk_file.trim();
    }
    // Nested replace shape: { site_walk_file: { url, filename } }
    if (v.site_walk_file && typeof v.site_walk_file === "object") {
      return fileUrlFromStoredValue(v.site_walk_file);
    }
  }
  return "";
}

function extensionFromAccept(accept) {
  const s = String(accept || "").trim();
  const m = s.match(/\.([a-z0-9]{2,8})/i);
  if (m) return `.${m[1].toLowerCase()}`;
  return ".pdf";
}

function labelFromFieldKey(fieldKey, accept) {
  const base = String(fieldKey || "").trim();
  if (!base) return "";
  if (/\.[a-z0-9]{2,8}$/i.test(base)) return base;
  return `${base}${extensionFromAccept(accept)}`;
}

function storedFilename(v) {
  if (!v || typeof v !== "object") return "";
  for (const key of ["filename", "originalFilename", "original_filename", "name"]) {
    const s = v[key];
    if (s != null && String(s).trim() && !/^https?:\/\//i.test(String(s).trim())) {
      return String(s).trim();
    }
  }
  if (v.site_walk_file && typeof v.site_walk_file === "object") {
    return storedFilename(v.site_walk_file);
  }
  return "";
}

/** Human label for a project field file (stored filename, field key + extension, or URL basename). */
export function fileDisplayLabelFromStoredValue(v, { fieldKey, accept } = {}) {
  const fromMeta = storedFilename(v);
  if (fromMeta) return fromMeta;

  const url = fileUrlFromStoredValue(v);
  if (url && /^https?:\/\//i.test(url)) {
    if (!/\/field-files\/\d+\//.test(url)) {
      try {
        const u = new URL(url);
        const name = u.pathname.split("/").filter(Boolean).pop();
        if (name) return decodeURIComponent(name);
      } catch {
        /* ignore */
      }
    }
    const fromKey = labelFromFieldKey(fieldKey, accept);
    if (fromKey) return fromKey;
  }

  if (fieldKey) return labelFromFieldKey(fieldKey, accept);

  if (/\.pdf$/i.test(url)) return "PDF document";
  if (/\.(kmz|kml)$/i.test(url)) return "Route file";
  return "Attached file";
}

export function openProjectFileUrl(url, { filename, accept } = {}) {
  const t = String(url || "").trim();
  if (!t) return;
  if (isProjectFieldFileContentUrl(t)) {
    void openAuthenticatedProjectFieldFile(t, { filename, accept });
    return;
  }
  window.open(t, "_blank", "noopener,noreferrer");
}

const FileGlyph = (
  <svg className="cell-file-icon-svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 12h8v2H8v-2zm0 4h8v2H8v-2z"
    />
  </svg>
);

/** Icon-only download/open control for permit and grid file cells. */
export function CommercialFieldFileIcon({ value, fieldKey, accept }) {
  const fileUrl = fileUrlFromStoredValue(value);
  const [opening, setOpening] = useState(false);
  if (!fileUrl) return <span className="project-detail-muted">—</span>;
  const title = fileDisplayLabelFromStoredValue(value, { fieldKey, accept });
  return (
    <button
      type="button"
      className="cell-file-link permits-file-icon-button"
      disabled={opening}
      onClick={() => {
        if (isProjectFieldFileContentUrl(fileUrl)) {
          setOpening(true);
          void openAuthenticatedProjectFieldFile(fileUrl, { filename: title, accept }).finally(() =>
            setOpening(false)
          );
          return;
        }
        openProjectFileUrl(fileUrl, { filename: title, accept });
      }}
      title={opening ? "Opening…" : title}
      aria-label={opening ? "Opening file" : `Open ${title}`}
    >
      {FileGlyph}
    </button>
  );
}

export default function CommercialFieldFileDisplay({ url, value, fieldKey, accept, displayName, label }) {
  const ref = value !== undefined ? value : url;
  const fileUrl = fileUrlFromStoredValue(ref);
  const [opening, setOpening] = useState(false);
  if (!fileUrl) return <span className="project-detail-muted">—</span>;

  const linkLabel = displayName || fileDisplayLabelFromStoredValue(ref, { fieldKey, accept });

  return (
    <div className="commercial-file-display">
      {label ? <span className="commercial-file-display-label">{label}</span> : null}
      <button
        type="button"
        className="commercial-file-view-link"
        disabled={opening}
        onClick={() => {
          if (isProjectFieldFileContentUrl(fileUrl)) {
            setOpening(true);
            void openAuthenticatedProjectFieldFile(fileUrl, {
              filename: linkLabel,
              accept,
            }).finally(() => setOpening(false));
            return;
          }
          openProjectFileUrl(fileUrl, { filename: linkLabel, accept });
        }}
      >
        {opening ? "Opening…" : linkLabel}
      </button>
    </div>
  );
}

export function CommercialFileList({ entries }) {
  if (!entries?.length) return null;
  return (
    <ul className="commercial-file-list">
      {entries.map((entry, idx) => (
        <li key={`${entry.label}-${idx}`} className="commercial-file-list-item">
          <CommercialFieldFileDisplay
            value={entry.value !== undefined ? entry.value : entry.url}
            url={entry.url}
            fieldKey={entry.fieldKey}
            accept={entry.accept}
            displayName={entry.displayName}
            label={entry.label}
          />
        </li>
      ))}
    </ul>
  );
}
